import type { BadgeVariant } from "@elit-hair/elit-ui";

import { DEFAULT_SETTING_LIST_CONFIG } from "@/pages/lists/utils/setting-list-config.utils";

import {
    TELLI_CALL_STATE,
    TELLI_WORKER_QUEUE_ITEM_STATUS,
    type PredefinedTelliContact,
    type TelliCallConnectionStatus,
    type TelliCallState,
    type TelliWorkerConfig,
    type TelliWorkerQueueItemStatus,
} from "../types/telli.types";

export const TELLI_TABS = {
    TRIGGER_CALL: "trigger-call",
    CALLS: "calls",
    WORKER: "worker",
} as const;

export const TELLI_TAB_VALUES = [TELLI_TABS.WORKER, TELLI_TABS.TRIGGER_CALL, TELLI_TABS.CALLS] as const;

export type TelliTab = (typeof TELLI_TAB_VALUES)[number];

export const TELLI_CONTACTS_PAGE_SIZE = 10;

export const PREDEFINED_TELLI_CONTACTS: PredefinedTelliContact[] = [
    {
        firstName: "Angel",
        lastName: "Zimmermann",
        phoneNumber: "+4915207624224",
        email: "a.zimmermann@elithair.de",
    },
    {
        firstName: "Vedran",
        lastName: "Tavic",
        phoneNumber: "+4917662042366",
        email: "vedran.tavic@elithair.de",
    },
];

export const TELLI_CALLS_PAGE_SIZE = 50;

export const TELLI_CALL_POLL_INTERVAL_MS = 5_000;

export const TELLI_CALL_STATE_LABELS: Record<TelliCallState, string> = {
    [TELLI_CALL_STATE.QUEUED]: "developer_telli_call_state_queued",
    [TELLI_CALL_STATE.RINGING]: "developer_telli_call_state_ringing",
    [TELLI_CALL_STATE.IN_PROGRESS]: "developer_telli_call_state_in_progress",
    [TELLI_CALL_STATE.PROCESSING]: "developer_telli_call_state_processing",
    [TELLI_CALL_STATE.ENDED]: "developer_telli_call_state_ended",
};

export const TELLI_CALL_STATE_BADGE_VARIANTS: Record<TelliCallState, BadgeVariant> = {
    [TELLI_CALL_STATE.QUEUED]: "secondary",
    [TELLI_CALL_STATE.RINGING]: "info",
    [TELLI_CALL_STATE.IN_PROGRESS]: "warning",
    [TELLI_CALL_STATE.PROCESSING]: "warning",
    [TELLI_CALL_STATE.ENDED]: "success",
};

export const TELLI_CALL_CONNECTION_STATUS_BADGE_VARIANTS: Record<TelliCallConnectionStatus, BadgeVariant> = {
    connected: "success",
    not_connected: "secondary",
    voicemail: "info",
    failed: "danger",
};

export function getTelliCallConnectionStatusBadgeVariant(status: string | null | undefined): BadgeVariant {
    if (status === "connected" || status === "not_connected" || status === "voicemail" || status === "failed") {
        return TELLI_CALL_CONNECTION_STATUS_BADGE_VARIANTS[status];
    }

    return "secondary";
}

export const TELLI_WORKER_QUEUE_ITEM_STATUS_LABELS: Record<TelliWorkerQueueItemStatus, string> = {
    [TELLI_WORKER_QUEUE_ITEM_STATUS.QUEUED]: "developer_telli_queue_status_queued",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.CONTACT_CREATED]: "developer_telli_queue_status_contact_created",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.CALL_INITIATED]: "developer_telli_queue_status_call_initiated",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.RINGING]: "developer_telli_queue_status_ringing",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.COMPLETED]: "developer_telli_queue_status_completed",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.RESOLVED]: "developer_telli_queue_status_resolved",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.FAILED]: "developer_telli_queue_status_failed",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.SKIPPED]: "developer_telli_queue_status_skipped",
};

export const TELLI_WORKER_QUEUE_ITEM_STATUS_BADGE_VARIANTS: Record<TelliWorkerQueueItemStatus, BadgeVariant> = {
    [TELLI_WORKER_QUEUE_ITEM_STATUS.QUEUED]: "secondary",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.CONTACT_CREATED]: "info",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.CALL_INITIATED]: "info",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.RINGING]: "warning",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.COMPLETED]: "success",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.RESOLVED]: "success",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.FAILED]: "danger",
    [TELLI_WORKER_QUEUE_ITEM_STATUS.SKIPPED]: "secondary",
};

export const TELLI_WORKER_PRIORITIES = [1, 2, 3, 4, 5, 6] as const;

export const DEFAULT_TELLI_WORKER_PRIORITIES: number[] = [];

export const DEFAULT_TELLI_WORKER_MAX_ATTEMPTS = 1;

export const TELLI_WORKER_MAX_ATTEMPTS_OPTIONS = [1, 2, 3, 4, 5] as const;

/** Radix Select sentinel for "no agent" (empty string is not a valid Select value). */
export const TELLI_AGENT_ROUTING_NONE_VALUE = "__none__";

export const DEFAULT_TELLI_WORKER_CONFIG: TelliWorkerConfig = {
    enabled: false,
    leadAssignation: {
        priorities: DEFAULT_TELLI_WORKER_PRIORITIES,
        listConfig: DEFAULT_SETTING_LIST_CONFIG,
    },
    agentRouting: {
        byStatus: {},
    },
    userAssignation: {
        userId: undefined,
    },
    maxAttempts: DEFAULT_TELLI_WORKER_MAX_ATTEMPTS,
};

export const TELLI_WORKER_QUEUE_SIZE = 3;
